/**
 * Handles the downloading of files, either via a request for the URL or via a single URL Endpoint
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import download from 'downloadjs';
import { ApAuthenticationContextConsumer } from '@alixpartners/ui-utils';
import { requestWithRetry, updateUrlToDemoMode } from 'utils/apiRequests';

import { getApiUrl, getRequestHeaders } from 'utils/api';

const DOWNLOAD_FILE_ERROR_MSG =
	'There was an error downloading the file. Please try again';
const REQUEST_FILE_ERROR_MSG =
	'There was an error trying to get a file url. Please try again';

export class FileExportComponent extends Component {
	static propTypes = {
		url: PropTypes.string.isRequired,
		onReady: PropTypes.func.isRequired,
		method: PropTypes.string,
		requestResponse: PropTypes.bool,
	};

	/**
	 * Get ahold of the request URL from another End Point
	 * e.g. {api end point} -> {response with file url} -> {download the file}
	 */
	getDownloadRequest = async headers => {
		const { onReady, url, fileName } = this.props;
		const method = this.props.method || 'GET';

		if (this.inProgress) return;
		this.inProgress = true;

		try {
			requestWithRetry(() =>
				fetch(getApiUrl(url), {
					method: method,
					headers: new Headers(headers),
				})
			)
				.then(res => {
					if (res.status !== 200) {
						throw new Error('bad response');
					}

					return res.json();
				})
				.then(res => {
					this.inProgress = false;
					const args =
						res.url && res.filename ? [res.url, res.filename] : [res, fileName];

					this.downloadFile({}, ...args);
				})
				.catch(e => {
					return onReady(REQUEST_FILE_ERROR_MSG);
				});
		} catch (e) {
			return onReady(REQUEST_FILE_ERROR_MSG);
		}
	};

	/**
	 * Take the url and download it to the browser, this includes any Authorisation that needs to take place
	 */
	downloadFile = async (headers, rUrl, filename) => {
		const { onReady } = this.props;
		const url = rUrl
			? updateUrlToDemoMode(rUrl)
			: updateUrlToDemoMode(getApiUrl(this.props.url));

		if (this.inProgress) {
			return;
		}

		this.inProgress = true;

		try {
			const response = await requestWithRetry(() =>
				fetch(url, {
					method: 'GET',
					headers: new Headers(headers),
				})
			);

			if (response.status !== 200) {
				throw new Error(DOWNLOAD_FILE_ERROR_MSG);
			}

			const file = await response.blob();
			download(file, filename);
			onReady();
		} catch (e) {
			return onReady(DOWNLOAD_FILE_ERROR_MSG);
		}
	};

	render() {
		return (
			<ApAuthenticationContextConsumer>
				{({ fetchAuthorizationToken }) => {
					if (this.isUnmounting) return;

					fetchAuthorizationToken().then(token => {
						const headers = getRequestHeaders(token);
						if (this.props.requestResponse) {
							this.getDownloadRequest(headers);
						} else {
							this.downloadFile(headers);
						}
					});
				}}
			</ApAuthenticationContextConsumer>
		);
	}
}
